import React, { useState, useEffect, useCallback } from "react";
import {
  Grid,
  Card,
  CardMedia,
  Container,
  CardContent,
  Typography,
  Box,
  Select,
  MenuItem,
  ButtonBase,
  CircularProgress,
  TextField,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import creditCards from "../JSON/creditCardData.json"; // JSON file
import Navbar from "./Navbar";
import Footer from "./Footer";
import "aos/dist/aos.css";
import Aos from "aos";

const BankingProductsScreen = () => {
  const navigate = useNavigate();


  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  useEffect(() => {
    Aos.init({ duration: 1000, once: false });
  }, []);

  // ------------------------
  // Filtering & Toggle States
  // ------------------------
  const [filter, setFilter] = useState("recommended");
  const [bankFilter, setBankFilter] = useState("");

  const handleFilterChange = (event, newFilter) => {
    if (newFilter !== null) setFilter(newFilter);
  };

  const handleBankFilterChange = (event) => {
    setBankFilter(event.target.value);
  };

  // ------------------------
  // Pincode Check States
  // ------------------------
  const [pincode, setPincode] = useState("");
  const [pincodeResult, setPincodeResult] = useState(null);
  const [pincodeLoading, setPincodeLoading] = useState(false);
  const [pincodeError, setPincodeError] = useState(null);

  // ------------------------
  // Infinite Scroll for JSON Cards
  // ------------------------
  const [visibleCards, setVisibleCards] = useState(6);
  const [isLoading, setIsLoading] = useState(false);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Navigate to card details page
  const handleCardClick = (id) => {
    navigate(`/credit-cards/${id}`);
  };

  // Filter JSON cards based on toggle and bank dropdown
  const filteredCards = creditCards
    .filter((card) => {
      if (filter === "recommended") return card.recommended === true;
      if (filter === "free") return card.type === "free";
      if (filter === "secured") return card.type === "secured";
      if (filter === "fuel") return card.type === "fuel";
      return true;
    })
    .filter((card) => {
      if (bankFilter) {
        const bankName = card.hero.title.split(" ")[0].toLowerCase();
        return bankName === bankFilter.toLowerCase();
      }
      return true;
    });

  // Infinite scroll logic (only for JSON cards when no pincode result exists)
  const loadMoreCards = useCallback(() => {
    if (filteredCards.length > visibleCards) {
      setIsLoading(true);
      setTimeout(() => {
        setVisibleCards((prev) => prev + 4);
        setIsLoading(false);
      }, 1000);
    }
  }, [filteredCards.length, visibleCards]);

  const handleScroll = useCallback(() => {
    if (!pincodeResult) {
      const scrollHeight = document.documentElement.scrollHeight;
      const scrollTop = document.documentElement.scrollTop;
      const clientHeight = window.innerHeight;
      if (scrollTop + clientHeight >= scrollHeight - 500 && !isLoading) {
        loadMoreCards();
      }
    }
  }, [pincodeResult, isLoading, loadMoreCards]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  // ------------------------
  // Pincode Check Function
  // ------------------------
  const handleCheckPincode = async () => {
    if (!pincode.trim()) return;
    setPincodeLoading(true);
    setPincodeError(null);
    setPincodeResult(null);
    try {
      const response = await fetch(`${BACKEND_URL}/check?pincode=${pincode}`);
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      const data = await response.json();
      setPincodeResult(data);
    } catch (err) {
      setPincodeError("Error checking pincode. Please try again.");
    } finally {
      setPincodeLoading(false);
    }
  };

  const handleClearPincode = () => {
    setPincode("");
    setPincodeResult(null);
    setPincodeError(null);
  };


  return (
    <Box>
      <Navbar />

      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          height: { xs: "30vh", sm: "50vh", md: "60vh" }, // responsive height
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          color: "#fff",
        }}
      >
        {/* Background Video */}
        <video
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          poster="https://ik.imagekit.io/arthgyan/Arthgyan_Web/imgproducts.mp4?updatedAt=1757133474187.jpg"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "center",
            zIndex: 0,
            backgroundColor: "#000",
            filter: "brightness(0.6)",
          }}
        >
          <source
            src="https://ik.imagekit.io/arthgyan/Arthgyan_Web/imgproducts.mp4?updatedAt=1757133474187"
            type="video/mp4"
          />
        </video>


        {/* Dark Overlay for readability */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0,0,0,0.4)", // transparent dark layer
            zIndex: 1,
          }}
        />

        {/* Content */}
        <Container maxWidth="md" sx={{ zIndex: 2 }}>
          <Typography
            variant={isSmallScreen ? "h5" : "h3"} // responsive font
            sx={{
              fontWeight: 800,
              color: "#fff",
              mt: { xs: 3, sm: 5, md: 5 },
              mb: { xs: 1, sm: 2, md: 2 },
              textShadow: "2px 2px 6px rgba(0,0,0,0.6)",
              fontSize: { xs: "15px", sm: "20px", md: "40px" }, // responsive font size

              // fontFamily: "Georgia, serif",
            }}
            data-aos="zoom-in"
          >
            Banking Products
          </Typography>

          <Typography
            sx={{
              color: "#fff",
              textShadow: "1px 1px 4px rgba(0,0,0,0.6)",
              mt: 2,
              px: { xs: 2, sm: 0 }, // small padding on mobile
              fontSize: { xs: "10px", sm: "16px", md: "18px" }, // responsive font size
            }}
          >
            Explore a wide range of credit cards, savings options, and banking solutions tailored for you.
          </Typography>

        </Container>
      </Box>











      {/* Pincode Check Section */}
      {/* <Box
        sx={{
          margin: 3,
          padding: 2,
          border: "1px solid #ccc",
          borderRadius: "8px",
          backgroundColor: "#fff",
        }}
      >
        <Typography variant="h6" gutterBottom>
          Check Available Credit Cards by Pincode
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, marginBottom: 2 }}>
          <TextField
            label="Enter Pincode"
            variant="outlined"
            value={pincode}
            onChange={(e) => setPincode(e.target.value)}
            size="small"
          />
          <Button variant="contained" onClick={handleCheckPincode}>
            Check
          </Button>
          {pincodeResult && (
            <Button variant="outlined" color="secondary" onClick={handleClearPincode}>
              Clear Pincode
            </Button>
          )}
        </Box>
        {pincodeLoading && <CircularProgress size={24} />}
        {pincodeError && (
          <Typography color="error" variant="body2">
            {pincodeError}
          </Typography>
        )}
        {pincodeResult && (
          <Box sx={{ marginTop: 2 }}>
            <Typography variant="subtitle1">Pincode: {pincodeResult.pincode}</Typography>
            <Typography variant="subtitle1">
              Available Banks: {pincodeResult.availableBanks.join(", ")}
            </Typography>
          </Box>
        )}
      </Box> */}

      {/* Show Filtering Options & JSON Cards only if no pincode result */}
      {!pincodeResult && (
        <Box
          sx={{
            width: "80%",
            marginX: "auto",
            padding: 3,
            // backgroundColor: "#f5f5f5",
            borderRadius: "16px",
            marginBottom: "1rem",
            boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
          }}
        >
          {/* Filtering Toggle Button & Bank Filter */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "1.5rem",
              flexWrap: "wrap",
              gap: "2rem",
            }}
          >
            <ToggleButtonGroup
              value={filter}
              exclusive
              onChange={handleFilterChange}
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: "8px",
                "& .MuiToggleButton-root": {
                  height: 40,
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                  borderRadius: "8px",
                  color: "#333",
                  borderColor: theme.palette.primary.main,
                  "&.Mui-selected": {
                    backgroundColor: "#0080a7",

                    // backgroundColor: theme.palette.primary.main,
                    color: "#fff",
                  },
                  "&:hover": {
                    backgroundColor: theme.palette.primary.dark,
                    color: "#fff",
                  },
                },
              }}
            >

              <ToggleButton value="recommended" aria-label="recommended">
                Recommended
              </ToggleButton>
              <ToggleButton value="free" aria-label="free">
                Life Time Free
              </ToggleButton>
              <ToggleButton value="secured" aria-label="secured">
                Secured Card
              </ToggleButton>
              <ToggleButton value="fuel" aria-label="fuel">
                Fuel
              </ToggleButton>
            </ToggleButtonGroup>
            <Select
              value={bankFilter}
              onChange={handleBankFilterChange}
              displayEmpty
              sx={{
                width: { xs: "100%", sm: 220 },
                borderRadius: "10px",
                "& fieldset": {

                  borderColor: theme.palette.primary.main,
                },
                "&:hover fieldset": {
                  borderColor: theme.palette.primary.dark,
                },
              }}
            >

              <MenuItem
                value=""
                sx={{
                  fontSize: "0.8rem",
                  "&:hover": {
                    backgroundColor: theme.palette.primary.main,
                    color: "#fff",
                  },
                }}
              >

                All Banks
              </MenuItem>
              {["SBI", "HDFC", "Axis", "IndusInd", "IDFC", "HSBC", "RBL"].map((bank) => (
                <MenuItem
                  key={bank}
                  value={bank}
                  sx={{
                    fontSize: "0.75rem",
                    "&:hover": {
                      backgroundColor: theme.palette.tertiary.main,
                      // background: "red",
                      color: "#117aae",
                    },
                  }}
                >
                  {bank}
                </MenuItem>
              ))}
            </Select>
          </Box>


          <Grid container spacing={3}>
            {filteredCards.slice(0, visibleCards).map((card) => (
              <Grid item xs={12} sm={6} md={6} key={card.id}>
                <Box
                  onClick={() => handleCardClick(card.id)}
                  sx={{
                    cursor: "pointer",
                    textAlign: "center",
                    mb: 3,
                  }}
                >
                  {/* Only Image */}
                  <Box
                    component="img"
                    src={card.card.image}
                    alt={card.hero.title}
                    loading="lazy"
                    sx={{
                      width: "100%",
                      height: "200px",
                      objectFit: "contain",
                      borderRadius: "16px",
                      mb: 1,
                    }}
                  />

                  {/* Card Title */}
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 700,
                      color: theme.palette.primary.main,
                      mb: 1,
                    }}
                  >
                    {card.hero.title}
                  </Typography>


                  {/* Benefits Text */}
                  <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        //                     px: 3,
                        //                     py: 1,
                        //                     borderRadius: "10px",
                        //                     fontWeight: 600,
                        //                     background: `linear-gradient(
                        //   90deg,
                        //   ${theme.palette.primary.main},
                        //   ${theme.palette.primary.dark}
                        // )`,
                        //                     color: "#fff",
                        //                     boxShadow: "0 6px 18px rgba(0,0,0,0.18)",




                        // mt: 2,
                        px: 3,
                        py: 1,
                        backgroundColor: "#0080a7",

                        // backgroundColor: theme.palette.primary.main,
                        color: "#fff",
                        fontWeight: 600,
                        borderRadius: "8px",
                        textTransform: "none",
                        "&:hover": {
                          backgroundColor: theme.palette.primary.dark,
                        },

                      }}
                    >
                      Benefits & Eligibility
                    </Typography>

                  </Box>

                </Box>
              </Grid>
            ))}
          </Grid>

          {isLoading && filteredCards.length > visibleCards && (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", marginTop: 3 }}>
              <CircularProgress color="primary" />
            </Box>
          )}
        </Box>
      )}

      {/* If pincodeResult exists, show API response cards */}
      {pincodeResult && (
        <Box
          sx={{
            width: "90%",
            marginX: "auto",
            padding: 3,
            backgroundColor: "#f5f5f5",
            borderRadius: "16px",
            marginBottom: "2rem",
            boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
          }}
        >
          <Grid container spacing={3}>
            {pincodeResult.availableCards.map((card) => (
              <Grid item xs={12} sm={6} md={3} key={card.id}>
                <ButtonBase
                  onClick={() => handleCardClick(card.id)}
                  sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <Card
                    sx={{
                      transition: "transform 0.2s, box-shadow 0.2s",
                      borderRadius: "16px",
                      boxShadow: 3,
                      backgroundColor: "#ffffff",
                      display: "flex",
                      flexDirection: "column",
                      height: "350px",
                      "&:hover": { transform: "scale(1.05)", boxShadow: 6 },
                    }}
                  >
                    <CardMedia
                      component="img"
                      height="200"
                      image={card.card.image}
                      alt={card.hero.title}
                      loading="lazy"
                      sx={{
                        borderTopLeftRadius: "16px",
                        borderTopRightRadius: "16px",
                        objectFit: "contain",
                        width: "100%",
                      }}
                    />
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: "800" }} gutterBottom>
                        {card.hero.title}
                      </Typography>
                    </CardContent>
                    <Box
                      sx={{
                        backgroundColor: "#1687c9",
                        borderBottomLeftRadius: "16px",
                        borderBottomRightRadius: "16px",
                        padding: 2,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: "bold", color: "white", textAlign: "center" }}>
                        Benefits & Eligibility
                      </Typography>
                    </Box>
                  </Card>
                </ButtonBase>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
      <Footer />
    </Box>
  );
};

export default BankingProductsScreen;

